import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {Kpi} from '../../../model/kpi';
import {Tenant} from '../../../model/tenant';
import {Organization} from '../../../model/organization';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Synoptic} from '../../../model/synoptics/synoptic';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {TenantsService} from '../../../services/tenants.service';
import {AuthGuard} from '../../../common/auth-guard.service';
import {SynopticsService} from '../../../services/synoptics.service';
import {AppConfig} from '../../../shared/global-variables';
import {noSpecialCharacters, noWhitespaces} from '../../../shared/custom-validators';
import {LoggedUser} from '../../../common/logged-user.service';
import {SynopticPersistRequest} from '../../../model/requests/synoptic-persist-request';
import {SynopticFile} from '../../../model/synoptics/synoptic-file';
import {CommonMessagesService} from '../../../services/common-messages.service';
import {DeviceMeasurement} from '../../../model/device-measurement';
import {AssetMeasurement} from '../../../model/asset-measurement';
import {DevicesService} from '../../../services/devices.service';
import {AssetsService} from '../../../services/assets.service';
import {KpisService} from '../../../services/kpis.service';
import {
  SynopticConfiguration,
  SynopticConfigurationComponent
} from '../../../model/synoptics/synoptic-configuration-types';
import {Device} from '../../../model/device';
import {FileData} from '../../../model/import-csv/file-data';
import {CsvSynopticData} from '../../../model/import-csv/csv-synoptic-data';
import {SynopticsFormFilesComponent} from './synoptics-form-files/synoptics-form-files.component';

@Component({
  selector: 'app-synoptics-form',
  templateUrl: './synoptics-form.component.html',
  styleUrls: ['./synoptics-form.component.scss']
})
export class SynopticsFormComponent implements OnInit {
  @ViewChild('files') fileComponent: SynopticsFormFilesComponent;

  public synoptic: Synoptic;
  public tenants: Tenant[];
  public organizations: Organization[];

  public synopticInfoForm: FormGroup;
  public synopticFiles: SynopticFile[];
  public synopticCsvFiles: FileData[];
  public csvData: CsvSynopticData[];
  public synopticConfiguration: string;
  public deviceMeasurements: DeviceMeasurement[];
  public assetMeasurements: AssetMeasurement[];
  public kpis: Kpi[];
  public devices: Device[];

  public configuration: SynopticConfiguration;

  public items: any[];
  public currentStep: number;
  public stepChanged: boolean;

  public persistingChanges: any;
  public formSuccess: boolean;
  public formLoaded: boolean;
  public validatingCsv: boolean;
  public processedCsv: boolean;
  public fromRoute: string;

  public mode: 'create' | 'edit';

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private i18n: TranslocoService,
    private tenantsService: TenantsService,
    private synopticsService: SynopticsService,
    private authGuard: AuthGuard,
    private loggedUser: LoggedUser,
    private commonMessagesService: CommonMessagesService,
    private devicesService: DevicesService,
    private assetsService: AssetsService,
    private kpisService: KpisService
  ) {
    this.deviceMeasurements = [];
    this.assetMeasurements = [];
    this.kpis = [];
    this.devices = [];
    this.synopticFiles = [];
    this.synopticCsvFiles = [];
    this.formLoaded = false;
    this.currentStep = 0;
    this.fromRoute = this.router.getCurrentNavigation()?.extras?.state?.fromRoute ?? 'synoptics';
    this.processedCsv = true;
    this.validatingCsv = false;
  }

  ngOnInit(): void {
    this.buildStepItems();
    this.route.url.subscribe(urlSegment => {
      const firstPath = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  private loadCreationForm(): void {
    this.synoptic = new Synoptic();
    if (!this.authGuard.superAdminRequired()) {
      this.synoptic.tenant = this.tenants[0];
    }
    this.createForm(this.synoptic);
    this.formLoaded = true;
  }

  private loadEditionForm(uuid): void {
    this.synopticsService.getSynoptic(uuid).subscribe(response => {
      this.synoptic = response.synoptic;
      this.synopticFiles = [...this.synoptic.files];
      this.synopticConfiguration = this.synoptic.configuration;
      this.parseSynopticConfiguration(this.synoptic.configuration);
      this.tenants = [this.synoptic.tenant];
      let uuids = this.synopticsService.findAllMeasurementUuidsInConfig(JSON.parse(this.synopticConfiguration));
      Promise.all([
        uuids.deviceMeasurementUuids.length > 0 ? this.loadDeviceMeasurements(uuids.deviceMeasurementUuids) : Promise.resolve(),
        uuids.assetMeasurementUuids.length > 0 ? this.loadAssetMeasurements(uuids.assetMeasurementUuids) : Promise.resolve(),
        uuids.kpiUuids.length > 0 ? this.loadKpiMeasurements(uuids.kpiUuids) : Promise.resolve(),
        uuids.deviceUuids.length > 0 ? this.loadDevices(uuids.deviceUuids) : Promise.resolve(),
        this.synopticsService.decodeSynopticFilesAndThumbnails(this.synopticFiles)
      ]).then((): void => {
        this.createForm(this.synoptic);
        this.formLoaded = true;
      });
    });
  }

  updateFromCsv(configuration: SynopticConfigurationComponent[]): void {
    this.clearEntities();
    this.processedCsv = false;
    const [firstConfig, ...childrenConfig] = configuration;
    this.configuration = {file: firstConfig.file, name: firstConfig.name, states: firstConfig.states,
      events: firstConfig.events,statics: firstConfig.statics, children: childrenConfig };
    if(this.fileComponent) {
      this.fileComponent.configuration = this.configuration;
    }
    this.synopticConfiguration = JSON.stringify(this.configuration);
    let uuids = this.synopticsService.findAllMeasurementUuidsInConfig(this.configuration);
    Promise.all([
      uuids.deviceMeasurementUuids.length > 0 ? this.loadDeviceMeasurements(uuids.deviceMeasurementUuids) : Promise.resolve(),
      uuids.assetMeasurementUuids.length > 0 ? this.loadAssetMeasurements(uuids.assetMeasurementUuids) : Promise.resolve(),
      uuids.kpiUuids.length > 0 ? this.loadKpiMeasurements(uuids.kpiUuids) : Promise.resolve(),
      uuids.deviceUuids.length > 0 ? this.loadDevices(uuids.deviceUuids) : Promise.resolve()
    ]).then((): void => {
      this.processedCsv = true;
    }).catch((): void => {
      this.processedCsv = true;
    });
  }

  private createForm(synoptic: Synoptic): void {
    this.synopticInfoForm = this.formBuilder.group({
      uuid: [{value: synoptic.uuid, disabled: true}],
      name: [synoptic.name, [
        Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH),
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters,
        noWhitespaces
      ]],
      description: [synoptic.description, [Validators.maxLength(255)]],
      tenant: [synoptic.tenant, [Validators.required]],
      organization: [{value: synoptic.organization, disabled: true}]
    });
    this.synopticInfoForm.markAsPending();

    if (!this.authGuard.superAdminRequired()) {
      this.synopticInfoForm.get('tenant').disable();
      this.loadOrganizations(synoptic.tenant.uuid).then(() => {
        this.loggedUser.userObservable.subscribe(user => {
          if (!user.organizationUuid) {
            this.synopticInfoForm.get('organization').enable();
          } else {
            this.synopticInfoForm.get('organization').setValue(this.organizations[0]);
            this.synopticInfoForm.get('organization').disable();
          }
        });
      });
    }
  }

  public onTenantChange(tenant: Tenant): void {
    this.clearMeasurements();
    if (tenant) {
      this.synopticInfoForm.get('organization').setValue(null);
      this.synopticInfoForm.get('organization').disable();
      this.loadOrganizations(tenant.uuid).then(() => {
        this.synopticInfoForm.get('organization').enable();
      });
    }
  }

  public onOrganizationChange(organization: Organization): void {
    this.clearMeasurements();
  }

  public onNextStep(): void {
    this.stepChanged = true;
    if (this.currentStep < this.items.length - 1) {
      this.currentStep++;
    }
  }

  public onPreviousStep(): void {
    this.stepChanged = true;
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  public onCancel(): void {
    this.router.navigate(([this.fromRoute]));
  }

  public persistSynopticData(): void {
    this.persistingChanges = true;
    const persistData: SynopticPersistRequest = this.buildPersistData();

    let promise: Promise<any>;
    if (this.mode === 'create') {
      promise = this.synopticsService.createSynoptic(persistData).toPromise();
    } else {
      promise = this.synopticsService.editSynoptic(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate(([`/synoptics/${this.mode === 'create' ? response.uuid : persistData.uuid}/details`]));
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  public parseSynopticConfiguration(synopticConfiguration: string): void {
    this.configuration = JSON.parse(synopticConfiguration);
  }

  private clearMeasurements(): void {
    this.deviceMeasurements = [];
    this.assetMeasurements = [];
    this.kpis = [];
    this.devices = [];
    this.synopticConfiguration = undefined;
  }

  private buildPersistData(): SynopticPersistRequest {
    const formValue = this.synopticInfoForm.getRawValue();
    return {
      name: formValue.name,
      description: formValue.description,
      configuration: this.synopticConfiguration,
      organizationUuid: formValue.organization?.uuid ?? null,
      tenantUuid: formValue.tenant.uuid,
      files: this.synopticFiles,
      uuid: this.mode === 'edit' ? this.synoptic.uuid : null
    };
  }

  private loadOrganizations(tenantUuid: string): Promise<void> {
    return this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise().then(value => {
      this.organizations = value.organizations;
    });
  }

  private loadDeviceMeasurements(uuids: string[]): Promise<any> {
    return this.devicesService.getDeviceMeasurements(uuids).toPromise().then(response => {
      this.deviceMeasurements = response.deviceMeasurements;
    });
  }

  private loadAssetMeasurements(uuids: string[]): Promise<any> {
    return this.assetsService.getAssetMeasurements(uuids).toPromise().then(response => {
      this.assetMeasurements = response.assetMeasurements;
    });
  }

  private loadKpiMeasurements(uuids: string[]): Promise<any> {
    return this.kpisService.getKpisFromUuids(uuids).toPromise().then(response => {
      this.kpis = response.kpis;
    });
  }

  private loadDevices(uuids: string[]): Promise<any> {
    return this.devicesService.getDevicesFromUuid(uuids).toPromise().then(response => {
      this.devices = response.devices;
    });
  }

  private clearEntities(): void {
    this.devices = [];
    this.kpis = [];
    this.assetMeasurements = [];
    this.deviceMeasurements = [];
  }

  private buildStepItems(): void {
    this.items = [
      {
        id: 'info',
        icon: 'pi pi-box',
        label: 'model.synoptic',
        description: 'forms.descriptions.synoptic'
      },
      {
        id: 'files',
        icon: 'pi pi-file',
        label: 'forms.files',
        description: 'forms.descriptions.synopticFiles'
      },
      {
        id: 'stateData',
        icon: 'pi pi-wifi',
        label: 'forms.stateData',
        description: 'forms.descriptions.stateData'
      },
      {
        id: 'eventData',
        icon: 'pi pi-tablet',
        label: 'forms.eventData',
        description: 'forms.descriptions.eventData'
      },
      {
        id: 'configuration',
        icon: 'pi pi-cog',
        label: 'forms.configuration',
        description: 'forms.descriptions.synopticConfiguration'
      },
      {
        id: 'confirmation',
        icon: 'pi pi-check',
        label: 'forms.confirmation'
      }
    ];
  }

  isAllCsvChecked(): boolean {
    return this.synopticCsvFiles.every(file => file.checked === true) && !this.validatingCsv && !this.processedCsv;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || (this.synopticInfoForm.untouched && this.currentStep === 0);
  }

}
